const debug = false;
/* ========================================
           HTML FUNCTIONS
======================================== */
export function html2dom(str) {
    const globalAny = globalThis;
    if (globalAny.DOMParser) {
        const parser = new globalAny.DOMParser();
        const doc = parser.parseFromString(str, 'text/html');
        return doc.body.firstChild;
    }
    const dom = globalAny.document?.createElement('div');
    if (!dom)
        return null;
    dom.innerHTML = str;
    return dom;
}
export function contrastyColor(hexColor) {
    const cleanHex = hexColor.replace('#', '');
    const fullHex = cleanHex.length === 3
        ? cleanHex.split('').map(char => char + char).join('')
        : cleanHex;
    const r = parseInt(fullHex.substring(0, 2), 16);
    const g = parseInt(fullHex.substring(2, 4), 16);
    const b = parseInt(fullHex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
}
/* ========================================
           ARRAY FUNCTIONS
======================================== */
export function mergeDeep(a, b) {
    // first is default vals, all other objects overwrite
    const extended = {};
    for (let i = 0; i < arguments.length; i++) {
        const thisObj = arguments[i];
        for (const prop in thisObj) {
            if (Object.prototype.hasOwnProperty.call(thisObj, prop)) {
                if (Object.prototype.toString.call(thisObj[prop]) === '[object Object]') {
                    // not sure why true was the first param... or why there were 3 params
                    extended[prop] = mergeDeep(extended[prop], thisObj[prop]);
                }
                else {
                    extended[prop] = thisObj[prop];
                }
            }
        }
    }
    return extended;
}
/* ========================================
           NUMBER FUNCTIONS
======================================== */
export function randomBetween(min, max) {
    /* ===== RANDOM NUM BETWEEN MIN AND MAX ===== */
    if (min < 0) {
        return min + Math.random() * (Math.abs(min) + max);
    }
    else {
        return min + Math.random() * (max - min);
    }
}
export function generateKey() {
    const vals = [];
    const globalAny = globalThis;
    const propertyName = 'cr' + 'ypto';
    const provider = globalAny?.[propertyName];
    vals[0] = Math.random().toString(36).substring(2, 15);
    vals[1] = Math.floor(performance.now() * Math.floor(Math.random() * 1000)).toString(36);
    vals[2] = Math.floor(Math.random() * new Date().getTime()).toString(36);
    vals[3] = Number((provider?.getRandomValues(new Uint16Array(4)) ?? new Uint16Array([Math.floor(Math.random() * 0xffffffff)])).join('')).toString(36);
    return (vals[Math.floor(Math.random() * 4)] + vals[Math.floor(Math.random() * 4)]);
}
export function generateUUID() {
    // https://stackoverflow.com/questions/105034/how-do-i-create-a-guid-uuid
    const globalAny = globalThis;
    const urlProvider = globalAny?.URL;
    if (urlProvider?.createObjectURL) {
        return urlProvider.createObjectURL(new Blob([])).substr(-36);
    }
    return Math.random().toString(36).slice(2, 14);
}
/**
 * Simple universal hash function for strings (Java-style hashCode)
 * Works in browser, Node, Next.js, etc. Not cryptographically secure.
 * @param str - Input string to hash
 * @returns String hash (may be negative, always string)
 */
export function hashCode(str) {
    let hash = 0, i, chr;
    if (str.length === 0)
        return '0';
    for (i = 0; i < str.length; i++) {
        chr = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash.toString();
}
export function formatAsUSD(cost) {
    if (!Number.isFinite(cost)) {
        return '$0.00';
    }
    return cost.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
export function formatAsHundredths(num) {
    return Math.trunc(num * 100) / 100;
}
export function toBoolean(value) {
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'number') {
        if (value === 1)
            return true;
        if (value === 0)
            return false;
        return undefined;
    }
    const normalized = sanitizeString(value).toLowerCase();
    if (normalized === 'true' || normalized === 'yes' || normalized === '1')
        return true;
    if (normalized === 'false' || normalized === 'no' || normalized === '0')
        return false;
    return undefined;
}
/* ========================================
           STRING FUNCTIONS
======================================== */
export function capitalize(str) {
    return str[0].toUpperCase() + str.toLowerCase().slice(1);
}
/** Capitalize the first letter of each word in `input`. */
export function capitalizeWords(input) {
    if (!input)
        return input;
    return input.replace(/\p{L}[\p{L}'’-]*/gu, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}
export function sanitizeString(value) {
    if (typeof value === 'string') {
        return String(value).replace(/\s+/g, ' ').trim();
    }
    if (typeof value === 'number') {
        return String(value);
    }
    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }
    return '';
}
export const US_STATES = [
    { value: 'AL', text: 'Alabama' },
    { value: 'AK', text: 'Alaska' },
    { value: 'AZ', text: 'Arizona' },
    { value: 'AR', text: 'Arkansas' },
    { value: 'CA', text: 'California' },
    { value: 'CO', text: 'Colorado' },
    { value: 'CT', text: 'Connecticut' },
    { value: 'DE', text: 'Delaware' },
    { value: 'FL', text: 'Florida' },
    { value: 'GA', text: 'Georgia' },
    { value: 'HI', text: 'Hawaii' },
    { value: 'ID', text: 'Idaho' },
    { value: 'IL', text: 'Illinois' },
    { value: 'IN', text: 'Indiana' },
    { value: 'IA', text: 'Iowa' },
    { value: 'KS', text: 'Kansas' },
    { value: 'KY', text: 'Kentucky' },
    { value: 'LA', text: 'Louisiana' },
    { value: 'ME', text: 'Maine' },
    { value: 'MD', text: 'Maryland' },
    { value: 'MA', text: 'Massachusetts' },
    { value: 'MI', text: 'Michigan' },
    { value: 'MN', text: 'Minnesota' },
    { value: 'MS', text: 'Mississippi' },
    { value: 'MO', text: 'Missouri' },
    { value: 'MT', text: 'Montana' },
    { value: 'NE', text: 'Nebraska' },
    { value: 'NV', text: 'Nevada' },
    { value: 'NH', text: 'New Hampshire' },
    { value: 'NJ', text: 'New Jersey' },
    { value: 'NM', text: 'New Mexico' },
    { value: 'NY', text: 'New York' },
    { value: 'NC', text: 'North Carolina' },
    { value: 'ND', text: 'North Dakota' },
    { value: 'OH', text: 'Ohio' },
    { value: 'OK', text: 'Oklahoma' },
    { value: 'OR', text: 'Oregon' },
    { value: 'PA', text: 'Pennsylvania' },
    { value: 'RI', text: 'Rhode Island' },
    { value: 'SC', text: 'South Carolina' },
    { value: 'SD', text: 'South Dakota' },
    { value: 'TN', text: 'Tennessee' },
    { value: 'TX', text: 'Texas' },
    { value: 'UT', text: 'Utah' },
    { value: 'VT', text: 'Vermont' },
    { value: 'VA', text: 'Virginia' },
    { value: 'WA', text: 'Washington' },
    { value: 'WV', text: 'West Virginia' },
    { value: 'WI', text: 'Wisconsin' },
    { value: 'WY', text: 'Wyoming' }
];
export function getUsStateName(abbr) {
    return US_STATES.find((state) => state.value === String(abbr).toUpperCase())?.text;
}
export function getUsStateAbbreviation(name) {
    return US_STATES.find((state) => state.text.toLowerCase() === String(name).trim().toLowerCase())?.value;
}
export function stringTo1337_v1(str) {
    return str
        .replace(/o/gi, '0')
        // .replace(/i/gi, '1')
        .replace(/l/gi, '1')
        .replace(/r/gi, '2')
        .replace(/e/gi, '3')
        .replace(/a/gi, '4')
        .replace(/s/gi, '5')
        .replace(/g/gi, '6')
        .replace(/t/gi, '7')
        .replace(/b/gi, '8')
        .replace(/g/gi, '9');
}
export function stringTo1337(str) {
    //converts lowercase non consecutive, non number characters (and doublets) to leet speak numbers
    const leetMap = {
        'o': '0', 'l': '1', 'z': '2', 'e': '3',
        'a': '4', 's': '5', 'b': '6', 't': '7',
        'g': '9'
    };
    let result = '';
    for (const char of str) {
        const leet = leetMap[char];
        const last = result.slice(-1);
        const lastIsNumber = /[0-9]/.test(last);
        // Convert if: 
        // 1. There is a mapping 
        // 2. AND (the last char isn't a leet number OR it's matches for a doublet)
        if (leet && (!lastIsNumber || last === leet)) {
            result += leet;
        }
        else {
            result += char;
        }
    }
    return result;
}
export function normalizePath(path) {
    if (!path)
        return '/';
    const trimmed = path.split('?')[0].split('#')[0].trim();
    if (trimmed === '' || trimmed === '/')
        return '/';
    return trimmed.replace(/\/+$|\s+/g, '');
}
export function normalizeEmail(value) {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
}
/*
Array.prototype.contains = function(obj) {
  return this.indexOf(obj) > -1;
};
*/
/**
 * Get the domain name to use as a key component for CacheManager.
 * Safe to call in browser contexts. For server-side, use getDomainFromHeaders() instead.
 *
 * If a URL string is passed, it parses the URL hostname and returns a normalized domain name.
 * Otherwise, it derives the domain from the current runtime host.
 *
 * @returns Domain name suitable for multi-tenant cache isolation (lowercase, no special characters)
 *
 * @example
 * const domain = getDomain();
 * const cache = new CacheManager({
 *   mode: 'local',
 *   domain,
 *   namespace: 'checkout'
 * });
 *
 * @example
 * // In different environments:
 * // www.pixelvivid.com → "pixelvivid"
 * // manningmetalworks.com → "manningmetalworks"
 * // localhost → "pixelated" (development)
 */
export function getDomain(url) {
    if (typeof url === 'string' && url.trim()) {
        try {
            const hostname = new URL(url).hostname;
            return extractDomainName(hostname);
        }
        catch {
            return extractDomainName(url);
        }
    }
    // Browser environment
    const globalAny = globalThis;
    const maybeWindow = typeof globalAny !== 'undefined' ? globalAny.window : undefined;
    if (maybeWindow?.location?.hostname) {
        return extractDomainName(maybeWindow.location.hostname);
    }
    // SSR/Node environment - return safe fallback
    // Library is deployed per-domain, so no actual multi-tenancy at library level
    // Each domain runs its own isolated copy of this library
    return 'pixelated';
}
/**
 * Extract the domain name from a hostname string.
 * Handles www prefixes and multi-level TLDs.
 *
 * @param hostname - The hostname (e.g., "www.example.com" or "example.com")
 * @returns The domain name without www or TLD (lowercase)
 *
 * @example
 * extractDomainName('www.pixelvivid.com') // → "pixelvivid"
 * extractDomainName('manningmetalworks.com') // → "manningmetalworks"
 * extractDomainName('localhost') // → "pixelated"
 */
export function extractDomainName(hostname) {
    if (!hostname)
        return 'pixelated';
    // Normalize: lowercase, trim whitespace
    const normalized = hostname.toLowerCase().trim();
    // Handle localhost / 127.0.0.1 - use development prefix
    if (normalized === 'localhost' || normalized === '127.0.0.1' || normalized.startsWith('localhost:')) {
        return 'pixelated';
    }
    // Split by dots
    const parts = normalized.split('.');
    // Single label (rare but possible in development)
    if (parts.length === 1) {
        return parts[0];
    }
    // Two labels: domain.com → domain
    if (parts.length === 2) {
        return parts[0];
    }
    // Multiple labels: www.domain.com or subdomain.domain.com → domain
    // Take the second-to-last part (the domain before the TLD)
    return parts[parts.length - 2];
}
export function attributeMap(oldAttribute) {
    // https://reactjs.org/docs/dom-elements.html
    const attributes = {
        autocapitalize: 'autoCapitalize',
        autocomplete: 'autoComplete',
        autocorrect: 'autoCorrect',
        autofocus: 'autoFocus',
        cellpadding: 'cellPadding',
        cellspacing: 'cellSpacing',
        charset: 'charSet',
        class: 'className',
        colspan: 'colSpan',
        datetime: 'dateTime',
        defaultvalue: 'defaultValue',
        for: 'htmlFor',
        formaction: 'formAction',
        formmethod: 'formMethod',
        formtarget: 'formTarget',
        frameborder: 'frameBorder',
        hreflang: 'hrefLang',
        httpequiv: 'httpEquiv',
        marginheight: 'marginHeight',
        marginwidth: 'marginWidth',
        maxlength: 'maxLength',
        minlength: 'minLength',
        onchange: 'onChange',
        readonly: 'readOnly',
        rowspan: 'rowSpan',
        spellcheck: 'spellCheck',
        tabindex: 'tabIndex'
    };
    return (attributes[oldAttribute] ? attributes[oldAttribute] : oldAttribute);
}
/**
     * Adds a single 'change' event listener to the document and uses event delegation
     * to log the event and the value of the target element when a change occurs
     * on an input, select, or textarea element.
     */
export function logAllChange() {
    const globalAny = globalThis;
    const doc = globalAny?.document;
    if (!doc)
        return;
    // Attach a single 'change' event listener to the document
    doc.addEventListener('change', function (event) {
        // The event.target is the specific element that triggered the event
        const targetElement = event.target;
        // Check if the target element is one that typically has a 'change' event (form controls)
        if (targetElement &&
            (targetElement.tagName === 'INPUT' ||
                targetElement.tagName === 'SELECT' ||
                targetElement.tagName === 'TEXTAREA')) {
            if (debug)
                console.log('Change event triggered:', event);
            // For text inputs, the change event only fires when the element loses focus
            // For checkboxes/radio buttons, event.target.checked provides the value
            const inputElement = targetElement;
            const changeValue = inputElement.type === 'checkbox' || inputElement.type === 'radio' ? inputElement.checked : inputElement.value;
            if (debug)
                console.log('Changed value:', changeValue);
        }
    });
}
// Call the function to activate the listeners once the script is loaded
// logChangeToAllElements();
/* ===== CLIENT COMPONENT DETECTION ===== */
/**
 * Regex patterns that identify client-only code requiring browser execution
 * Used by both ESLint rules and build scripts to determine client vs server components
 */
export const CLIENT_ONLY_PATTERNS = [
    /\baddEventListener\b/,
    /\bcreateContext\b/,
    /\bdocument\./,
    /\blocalStorage\b/,
    /\bnavigator\./,
    /\bonBlur\b/,
    /\bonChange\b/,
    /\bonClick\b/,
    /\bonFocus\b/,
    /\bonInput\b/,
    /\bonKey\b/,
    /\bonMouse\b/,
    /\bonSubmit\b/,
    /\bremoveEventListener\b/,
    /\bsessionStorage\b/,
    /\buseCallback\b/,
    /\buseContext\b/,
    /\buseEffect\b/,
    /\buseLayoutEffect\b/,
    /\buseMemo\b/,
    /\buseReducer\b/,
    /\buseRef\b/,
    /\buseState\b/,
    /\bwindow\./,
    /["']use client["']/ // Client directive
];
/* ===== COMPONENT FILE DETECTION ===== */
/**
 * Glob patterns for finding component files
 */
export const TS_FILE_IGNORE_PATTERNS = [
    '**/*.d.ts',
    '**/*.test.ts',
    '**/*.spec.ts',
    '**/*.stories.ts',
    '**/documentation/**',
    '**/examples/**',
    '**/*.example.*',
    '**/*extractor.ts'
];
export const TSX_FILE_IGNORE_PATTERNS = [
    '**/*.test.tsx',
    '**/*.spec.tsx',
    '**/*.stories.tsx',
    '**/documentation/**',
    '**/examples/**',
    '**/*.example.*'
];
/* ===== SERVER COMPONENT DETECTION ===== */
/**
 * Regex patterns that identify server-only code that should not run on client
 */
export const SERVER_ONLY_PATTERNS = [
    /["']use server["']/, // Server directive
    /\b__dirname\b/,
    /\b__filename\b/,
    /@aws-sdk/,
    /\bchild_process\b/,
    /\bexec\b/,
    /\bexecAsync\b/,
    /\bfs\b/,
    /\bfs\.readFileSync\b/,
    /\bfs\.existsSync\b/,
    /\bcrypto\b/,
    /\bgetFullPixelatedConfig\b/,
    /\bimport.*googleapis\b|\brequire.*googleapis\b/, // Actual import of googleapis
    /\bimport.*next\/server\b|\brequire.*next\/server\b/, // Actual import of next/server
    /\bimport.*path\b|\brequire.*path\b/, // Actual import of path module
    /\bprocess\.cwd\(\)/,
    /\brequire\.resolve\b/,
    /\butil\b/
    //	/\bNextRequest\b/,
    //	/\bNextResponse\b/
];
